import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../api/dates";
import { clock, dayColor, fmtDuration, fmtKm, type PlannedDay, type Proposal } from "../../api/routing";
import { StatusChip } from "../../components/ui";
import type { DayCrews } from "./useDayCrews";

type Providers = readonly { id: number; name: string }[];

/** The proposed days, one card each in the day's map colour: the stops in
 * order with their arrival times, and who works the day and when. */
export default function ProposalDays({
  proposal,
  providers,
  crews,
  locked,
}: {
  proposal: Proposal;
  providers: Providers;
  crews: DayCrews;
  /** Accepted: the days belong to the crews now, and move on the plan. */
  locked: boolean;
}) {
  return (
    <div className="day-cards">
      {proposal.days.map((day) => (
        <DayCard key={day.day_index} day={day} providers={providers} crews={crews} locked={locked} />
      ))}
    </div>
  );
}

function DayCard({ day, providers, crews, locked }: { day: PlannedDay; providers: Providers; crews: DayCrews; locked: boolean }) {
  const n = day.day_index + 1;
  const choice = crews.choices[day.day_index] ?? { providerId: "", date: "" };
  const error = crews.errors[day.day_index];
  return (
    <article className="day-card" style={{ "--day": dayColor(day.day_index) } as CSSProperties} aria-label={`Day ${n}`}>
      <header className="day-card-head">
        <strong>Day {n}</strong>
        <span>
          {day.stops.length} site{day.stops.length === 1 ? "" : "s"} · {fmtKm(day.total_m)} km · {fmtDuration(day.total_s)} driving
        </span>
        {day.day_type === "weekend" ? <StatusChip tone="warn">Weekend</StatusChip> : null}
      </header>
      <ol className="day-stops">
        {day.stops.map((s) => (
          <li key={s.site_id} className={s.flagged ? "flagged" : undefined}>
            <span className="stop-seq">{s.seq}</span>
            <span className="stop-name">
              <Link to={`/sites/${s.site_id}`}>{s.name}</Link>
              <small>
                {fmtKm(s.leg_m)} km · {fmtDuration(s.leg_s)}
                {s.flagged ? " · flagged" : ""}
              </small>
            </span>
            <span className="stop-time">
              {clock(s.arrive_min)}–{clock(s.depart_min)}
            </span>
          </li>
        ))}
      </ol>
      <div className="day-card-foot">
        <select
          className={choice.providerId === "" ? "select missing" : "select"}
          aria-label={`Field operators for day ${n}`}
          value={choice.providerId}
          disabled={locked}
          onChange={(e) => crews.choose(day.day_index, { ...choice, providerId: e.target.value })}
        >
          <option value="">Choose a crew…</option>
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <input
          className="input"
          type="date"
          aria-label={`Date for day ${n}`}
          value={choice.date}
          disabled={locked}
          onChange={(e) => crews.choose(day.day_index, { ...choice, date: e.target.value })}
        />
        {crews.isPending(day.day_index) ? <span className="day-assigned">Assigning…</span> : null}
        {crews.isAssigned(day.day_index) ? (
          <span className="day-assigned" title={`Assigned for ${formatDate(choice.date)}`}>
            ✓ Assigned
          </span>
        ) : null}
        {error ? <p className="notice bad">{error}</p> : null}
      </div>
    </article>
  );
}
